// ESLint Settings
/* eslint complexity: "off", no-useless-escape: "off" */
/* jshint bitwise: false */
/* exported platformCheck */

/**
* @author FireFall
* @version 0.0.3
*
* Changelog
* 0.0.3
* Added NodeList and Array handling
* 0.0.2
* - Added polyfills for event handling and Object.assign
*/

/**
* @name platformCheck
* @description Sets and updates platform related values, the latter if resize events is set to be checked
* @param {object} options - Options to customize platformCheck functionality
*   @param {HTMLElement} options.target - DOM Target to add classes to
*   @param {integer} [options.mobileSize] - Size for desktop (if resize tracking is enabled) where it's considered mobile
*   @param {boolean} [options.trackResize=true] - Whether to attach a resize event, this tracks if desktop has entered/left mobile size on my default
*   @param {integer} [options.trackResizeTimeout=500] - Timeout throttle for resize event defaults to 500 ms
*   @param {string} [options.mobileClass='mobile'] - Mobile class to append if options.target is set
*   @param {string} [options.ipadClass='ipad'] - iPad class to append if options.target is set
*   @param {string} [options.desktopClass='desktop'] - Desktop class to append if options.target is set
*   @param {string} [options.ieClass='ie'] - IE Class to append if options.target is set. This adds two classes, ie and ie<version number>
*   @param {function} options.isMobileCallback - Mobile callback to modify platformVars.isMobile. Function must return a boolean
*   @param {function} options.isIpadCallback - iPad callback to modify platformVars.isIpad. Function must return a boolean
*   @param {function} options.isDesktopCallback - Desktop callback to modify platformVars.isDesktop. Function must return a boolean
*   @param {function} options.isIECallback - IE Callback to modify platformVars.isIE. Function must return a integer value
* @returns {object} An object whose properties represent the current platform the script is run
*/
var platformCheck = (function() {
  'use strict';

  var platformCheck = function(options) {
    var defaults = {
      target: undefined, // DOM target to apply classes to
      mobileSize: undefined, // In PX width
      trackResize: true,
      trackResizeTimeout: 500,
      mobileClass: 'mobile',
      ipadClass: 'ipad',
      desktopClass: 'desktop',
      ieClass: 'ie', // Append ie version to this
      // Callbacks for developer implementation of platform verification
      // Called AFTER initial checks
      // Mobile, iPad, and Desktop must return boolean values
      // IE is an exception as it can and should take numbers
      isMobileCallback: undefined,
      isIpadCallback: undefined,
      isDesktopCallback: undefined,
      isIECallback: undefined
    };
    var platformVars = {
      isMobile: (/iphone|ipod|android|blackberry|mini|windows\sce|palm/i).test(navigator.userAgent.toLowerCase()),
      isIpad: navigator.userAgent.toLowerCase().match(/ipad/i) !== null,
      isDesktop: false,
      isIE: false,
      orientation: undefined // False for landscape, true for portrait iPad only feature, null if not iPad
    };
    // Function that creates (throttled) resize event listeners
    var resizeHandler = (function() {
      var resizeTimeout;

      return function() {
        var width = window.innerWidth;
        var height = window.innerHeight;

        if (typeof resizeTimeout !== 'undefined') {
          clearTimeout(resizeTimeout);
        }

        resizeTimeout = setTimeout (function() {
          var flag = 0;

          // Desktop mobile dimension handling
          if (platformVars.isDesktop && typeof options.mobileSize !== 'undefined') {
            if (width > options.mobileSize && platformVars.isMobile) {
              platformVars.isMobile = false;
              flag = platformCheck.FLAG_MOBILE;
            } else if (width <= options.mobileSize && !platformVars.isMobile) {
              platformVars.isMobile = true;
              flag = platformCheck.FLAG_MOBILE;
            }
          }

          // Track orientation
          if (platformVars.isIpad) {
            if (width > height && (platformVars.orientation || typeof platformVars.orientation === 'undefined')) {
              platformVars.orientation = false;
              flag = flag | platformCheck.FLAG_ORIENTATION;
            } else if (!platformVars.orientation) {
              platformVars.orientation = true;
              flag = flag | platformCheck.FLAG_ORIENTATION;
            }
          }

          if (flag !== 0) {
            triggerEvent(window, 'platformcheck-update', {flag: flag, platformVars: platformVars});
          }

          resizeTimeout = undefined;
        }, options.trackResizeTimeout);
      };
    })();

    // Polyfill for Object.assign
    function extend(defaults, options) {
      var prop;
      var result = {};

      for (prop in defaults) {
        // If options has property
        if (options.hasOwnProperty(prop)) {
          result[prop] = options[prop];
        } else if (defaults.hasOwnProperty(prop)) { // Otherwise use defaults
          result[prop] = defaults[prop];
        }
      }

      return result;
    }

    function triggerEvent(target, eventName, values) {
      var event;

      // Fallback for browsers where Event and CustomEvent have no constructors
      if (typeof Event !== 'function' || typeof CustomEvent !== 'function') {
        if (typeof values !== 'object') {
          event = document.createEvent('HTMLEvents');
          event.initEvent(eventName, true, true);
        } else {
          event = document.createEvent('CustomEvent');
          event.initCustomEvent(eventName, true, true, values);
        }
      } else {
        if (typeof values !== 'object') {
          event = new Event(eventName);
        } else {
          event = new CustomEvent(eventName, {detail: values});
        }
      }

      target.dispatchEvent(event);
    }

    /*
    * @function getInternetExplorerVersion
    * @description Returns the version of Internet Explorer or a -1 (indicating the use of another browser).
    * @see: https://msdn.microsoft.com/en-us/library/ms537509(v=vs.85).aspx
    * @returns {Number} -1 if not any IE version, any other number otherwise
    */
    function getInternetExplorerVersion() {
      var version = false;
      var userAgent = navigator.userAgent;
      var appName = navigator.appName;
      var regex;

      // works up to IE 10 but IE 11 spoofs this value. seperate check for IE Edge
      if (appName === 'Microsoft Internet Explorer') {
        regex = /MSIE ([0-9]{1,}[\.0-9]{0,})/;

        if (regex.exec(userAgent) !== null) {
          version = parseInt(RegExp.$1);
        }
      } else if (appName === 'Netscape' && userAgent.indexOf('Trident') > -1) { // IE 11
        version = 11;
      } else if (appName === 'Netscape' && userAgent.indexOf('Edge') > -1) { // IE Edge
        regex = /Edge\/([0-9]{1,}[\.0-9]{0,})/;

        if (regex.exec(userAgent) !== null) {
          version = parseInt(RegExp.$1);
        }
      }
      return version;
    }

    function handleTarget(target, options) {
      if (target instanceof HTMLElement) {
        if (platformVars.isMobile) {
          target.classList.add(options.mobileClass);
        }

        if (platformVars.isIpad) {
          target.classList.add(options.ipadClass);
        }

        if (platformVars.isDesktop) {
          target.classList.add(options.desktopClass);
        }

        // Marks browser as IE, as well as version number
        if (typeof platformVars.ie !== 'undefined') {
          target.classList.add(options.ieClass);
          target.classList.add(options.ieClass + platformVars.isIE);
        }
      }
    }

    if (typeof Object.assign !== 'undefined') {
      options = Object.assign({}, defaults, options);
    } else {
      options = extend(defaults, options);
    }

    platformVars.isDesktop = !platformVars.isMobile;
    platformVars.isIE = getInternetExplorerVersion();

    // Modifier callback for mobile
    if (typeof options.isMobileCallback === 'function') {
      platformVars.isMobile = options.isMobileCallback();

      // Set mobile state if mobileSize is true
      if (options.mobileSize && window.innerWidth <= options.mobileSize) {
        platformVars.isMobile = true;
      }
    }

    // Modifier callback for iPad
    if (typeof options.isIpadCallback === 'function') {
      platformVars.isIpad = options.isIpadCallback();
    }

    // Modifier callback for desktop
    if (typeof options.isDesktopCallback === 'function') {
      platformVars.isDesktop = options.isDesktopCallback();
    }

    // Modifier callback for IE
    if (typeof options.isIECallback === 'function') {
      platformVars.isIE = options.isIECallback();
    }

    // Add classes to target DOM element
    if (typeof options.target !== 'undefined') {
      if (options.target instanceof HTMLElement) {
        handleTarget(options.target, options);
      } else if (options.target instanceof NodeList || options.target instanceof Array) {
        // Iterate over NodeList or Array
        (function() {
          var i = 0;
          var node;

          for (i = 0; i < options.target.length; i++) {
            node = options.target[i];
            handleTarget(node, options);
          }
        })();
      }
    }

    if (options.trackResize) {
      // Remove previous resize tracking event calls. There can be only one
      window.removeEventListener('resize', resizeHandler);
      window.onresize = resizeHandler;

      // Force update after event is created
      triggerEvent(window, 'resize');
    }

    // Create a clone of this
    return platformVars;
  };

  platformCheck.FLAG_MOBILE = 1;
  platformCheck.FLAG_ORIENTATION = 2;

  return platformCheck;
})();
